<template>
  <DetailLayout @close="$emit('close')">
    <!-- No `title` prop: the `#header` slot replaces DetailHeader's whole title
             area, which is the only thing `title` feeds. -->
    <template #header>
      <div class="flex items-center gap-8">
        <MetalImage :metal :size="36" />
        <h2 class="raw-heading-3">
          {{ i18n(metal.name) }}
          <span v-if="metal.symbol" class="font-[400] text-[#808080]">
            ({{ metal.symbol }})
          </span>
        </h2>
      </div>
    </template>

    <!-- Trailing header controls, supplied by the caller (the panel passes its
             extend button; the reader passes nothing — inside, only the ✕). -->
    <template v-if="$slots.actions" #actions>
      <slot name="actions" />
    </template>

    <div :key="metal.slug" class="grid gap-24">
      <!-- Video, or the hero photo in its place — never both. -->
      <PhotoFrame v-if="photos.hero" :photo="photos.hero" />

      <NativeVideo
        v-if="metal.video"
        :id="metalVideoId(metal.slug)"
        :sources="sourcesFor(metal.video.name)"
        :poster="metal.video.poster"
        :title="i18n(metal.name)"
        @play="handlePlayVideo()"
      />

      <!-- Quick fact -->
      <QuickFact v-if="quickFact" :fact="quickFact" :zone />

      <!-- The triple cost -->
      <section v-if="impactEntries.length" class="grid gap-4">
        <ScoredAccordion
          v-for="entry in impactEntries"
          :key="entry.type"
          :name="`impact-${entry.type}`"
          :icon="entry.icon"
          :label="entry.label"
          :score="entry.section.severity"
          :summary="entry.section.summary"
          :blocks="entry.section.blocks"
          :open="openImpact === entry.type"
          :group-open="openImpact !== null"
          :marker="slotMarkers[`${entry.type}Score`]"
          :zone
          @toggle="(open) => (openImpact = open ? entry.type : null)"
        />
      </section>

      <!-- In your phone -->
      <SmartphoneSection v-if="smartphone" :info="smartphone" />

      <!-- What it does -->
      <section v-if="whatItDoes" class="grid gap-8">
        <h3 class="raw-cta-2">
          {{ i18n(translations.whatItDoes) }}
        </h3>
        <p>
          <InlineMarkdown :message="whatItDoes.text" /><SourceMarker
            :refs="whatItDoes.sourceRefs"
            :zone
          />
        </p>
      </section>

      <!-- How it is extracted -->
      <MetalAccordion v-if="extraction" sticky>
        <template #header>
          <RevIllustration
            src="/mine-my-phone/icons/pickaxe--emoji.svg"
            :width="18"
            :height="18"
            alt=""
            aria-hidden="true"
            class="aspect-square object-cover"
          />
          <span class="raw-cta-2">
            {{ i18n(translations.extraction) }}
          </span>
        </template>
        <ContentBlocks :blocks="extraction" :zone />
      </MetalAccordion>

      <!-- Top mining countries -->
      <section v-if="metal.countries.length" class="grid gap-12">
        <h3 class="raw-cta-2">
          {{ i18n(translations.topMiningCountries)
          }}<FootnoteMarker :marker="slotMarkers.countries" />
        </h3>
        <MinePills :countries="metal.countries" />
      </section>

      <!-- Photography, first bento -->
      <PhotoBento v-if="photos.firstBento.length" :photos="photos.firstBento" />

      <!-- Can we keep up? — one heading over both, and no image strip between
                 them; `--score-fill` inherits down to recycling's `ScoreCircles`. -->
      <section v-if="supplyRisk || recycling" class="grid gap-12">
        <h3 class="raw-cta-2">
          {{ i18n(translations.keepUp) }}
        </h3>
        <div class="grid gap-4">
          <ScoredAccordion
            v-if="supplyRisk"
            name="supply-risk"
            :label="translations.supplyRisk"
            :score="supplyRisk.score"
            :summary="supplyRisk.summary"
            :blocks="supplyRisk.blocks"
            :open="openRisk === 'supplyRisk'"
            :group-open="openRisk !== null"
            :marker="slotMarkers.supplyRiskScore"
            :zone
            @toggle="(open) => (openRisk = open ? 'supplyRisk' : null)"
          />
          <ScoredAccordion
            v-if="recycling"
            class="[--score-fill:white]"
            name="recycling"
            :label="translations.recycling"
            :score="recycling.score"
            :summary="recycling.summary"
            :blocks="recycling.blocks"
            :open="openRisk === 'recycling'"
            :group-open="openRisk !== null"
            :marker="slotMarkers.recyclingScore"
            :zone
            @toggle="(open) => (openRisk = open ? 'recycling' : null)"
          />
        </div>
      </section>

      <!-- Photography, second bento -->
      <PhotoBento
        v-if="photos.secondBento.length"
        :photos="photos.secondBento"
      />

      <ShareButton :zone class="justify-self-start" />

      <TakeActionCallout :zone />

      <!-- The same row the impact panel opens the journey from. -->
      <JourneyEntry v-if="journey" :journey :zone />

      <hr
        class="h-[1px] border-0 bg-[black]/15 [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]"
      />

      <!-- Related metals (authored on the metal, not inferred) -->
      <RelatedMetals :metal-slug="metal.slug" :zone />

      <!-- Related stories, sharing the separator above: one block of links. -->
      <RelatedArticles :metal-slug="metal.slug" :zone />

      <!-- Footnotes, last of all — where the article view puts its sources, and where
                 the client's sheets put these. -->
      <section v-if="footnotes.length" class="grid gap-8">
        <h3 class="raw-cta-2">
          {{ i18n(translations.footnotes) }}
        </h3>
        <FootnotesList :footnotes :zone />
      </section>

      <!-- Photography credits: flat and deduplicated, so they map to no one photo. -->
      <section v-if="photos.credits.length" class="grid gap-8">
        <h3 class="raw-cta-2">
          {{ i18n(translations.photoCredits) }}
        </h3>
        <ul
          class="raw-body-3 grid list-none gap-8 text-[var(--raw-darkgrey)] [overflow-wrap:anywhere]"
        >
          <li
            v-for="credit in photos.credits"
            :key="credit"
            class="leading-[1.2]"
          >
            {{ credit }}
          </li>
        </ul>
      </section>
    </div>
  </DetailLayout>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevIllustration } from '@ds/components/Illustration'

import DetailLayout from '~/scopes/campaign/components/UI/DetailLayout.vue'
import FootnoteMarker from '~/scopes/campaign/components/UI/FootnoteMarker.vue'
import InlineMarkdown from '~/scopes/campaign/components/UI/InlineMarkdown.vue'
import MetalImage from '~/scopes/campaign/components/UI/MetalImage.vue'
import NativeVideo from '~/scopes/campaign/components/UI/NativeVideo.vue'
import ShareButton from '~/scopes/campaign/components/UI/ShareButton.vue'
import SourceMarker from '~/scopes/campaign/components/UI/SourceMarker.vue'
import ContentBlocks from '~/scopes/campaign/components/highlights/metal/ContentBlocks.vue'
import FootnotesList from '~/scopes/campaign/components/highlights/metal/FootnotesList.vue'
import MetalAccordion from '~/scopes/campaign/components/highlights/metal/MetalAccordion.vue'
import MinePills from '~/scopes/campaign/components/highlights/metal/MinePills.vue'
import PhotoBento from '~/scopes/campaign/components/highlights/metal/PhotoBento.vue'
import PhotoFrame from '~/scopes/campaign/components/highlights/metal/PhotoFrame.vue'
import QuickFact from '~/scopes/campaign/components/highlights/metal/QuickFact.vue'
import RelatedArticles from '~/scopes/campaign/components/highlights/metal/RelatedArticles.vue'
import RelatedMetals from '~/scopes/campaign/components/highlights/metal/RelatedMetals.vue'
import ScoredAccordion from '~/scopes/campaign/components/highlights/metal/ScoredAccordion.vue'
import SmartphoneSection from '~/scopes/campaign/components/highlights/metal/SmartphoneSection.vue'
import JourneyEntry from '~/scopes/campaign/components/journey/JourneyEntry.vue'
import TakeActionCallout from '~/scopes/campaign/components/takeAction/TakeActionCallout.vue'
import { useVideoSources } from '~/scopes/campaign/composables/useVideoSources'
import navTranslations from '~/scopes/campaign/const/impact-navigation.translations'
import { journeyForMetal } from '~/scopes/campaign/data/journeys/data'
import { footnoteSlotMarkers } from '~/scopes/campaign/data/metals/footnotes'
import { metalPhotos } from '~/scopes/campaign/data/metals/photos'
import type { ImpactType, Metal } from '~/scopes/campaign/data/metals/types'
import { metalVideoId } from '~/scopes/campaign/data/videos'

import translations from './MetalView.translations'

// The whole metal view — header included — shared verbatim by the narrow panel
// (`MetalDetail`, itself mounted twice: the library detail and the `?metal=` quick view)
// and the extended reader (`ExtendedReader`). The reader isn't a variant of the metal,
// it's the same metal in a wider container, so this stays a dumb view: no
// `useModalState`, and deliberately no `trackPageView` (its callers can be mounted at
// once, so a page view here would fire twice — each adapter owns its own).
//
// The template's root element must stay alone — no sibling comment, nothing. A second
// root-level node makes the render root a fragment, and this view is a `<Transition>`
// child (ContentSwap) in all three mounts: the leaving copy is then never removed from
// the DOM, so swapped metals pile up. Dev-only, since the production SFC build strips
// comments — which is precisely why it's easy to reintroduce. Hence this note living
// here, not there. Same reasoning as `impacts/article/ArticleView.vue`.
const props = defineProps<{
  metal: Metal
  /** Tracking surface for the CTAs inside (`metal-detail` / `metal-extended`). */
  zone: string
}>()

defineEmits<{ close: [] }>()

const i18n = useI18n()
const { trackClick } = useTracking()
const { sourcesFor } = useVideoSources()

// Order, icon and label of the impact accordions; `ScoredAccordion` serves the scored
// pair too and knows nothing about impact types. Labels are the impacts nav's, shared.
const IMPACTS = [
  {
    type: 'environmental',
    icon: '/mine-my-phone/icons/plant--emoji.svg',
    label: navTranslations.environmentalImpactButton,
  },
  {
    type: 'human',
    icon: '/mine-my-phone/icons/group--emoji.svg',
    label: navTranslations.humanImpactButton,
  },
  {
    type: 'geopolitical',
    icon: '/mine-my-phone/icons/world--emoji.svg',
    label: navTranslations.geopoliticalImpactButton,
  },
] as const

// Each content section resolves to a value-or-empty so the template gates on a
// plain ref (reliable vue-tsc narrowing) rather than deep optional chains.
const content = computed(() => props.metal.content ?? null)
const smartphone = computed(() => content.value?.smartphone ?? null)
// Three metals have a journey; the rest resolve to nothing and show no row.
const journey = computed(() => journeyForMetal(props.metal.slug))
const whatItDoes = computed(() => content.value?.whatItDoes ?? null)
const extraction = computed(() => content.value?.extraction ?? null)
const quickFact = computed(() => content.value?.quickFact ?? null)
const supplyRisk = computed(() => content.value?.supplyRisk ?? null)
const recycling = computed(() => content.value?.recycling ?? null)
// Frames the scored pair instead of interrupting it; hero and credits resolve here too.
const photos = computed(() =>
  metalPhotos(content.value?.photos, !!props.metal.video),
)
const footnotes = computed(() => content.value?.footnotes ?? [])
const impactEntries = computed(() => {
  const impacts = content.value?.impacts
  if (!impacts) return []
  return IMPACTS.map((meta) => ({ ...meta, section: impacts[meta.type] }))
})

// Markers the notes claim on labels this metal doesn't own — the countries heading and the
// score rows — so a `[^*]` token can't be written into them. Each lands on its label, not on
// the score dots: an asterisk after five dots has no word to attach to.
const slotMarkers = computed(() => footnoteSlotMarkers(footnotes.value))

// Two independent single-open groups: the scored pair sits sections away from the
// impacts, so sharing one state would fold something the reader has scrolled past.
const openImpact = ref<ImpactType | null>(null)
const openRisk = ref<'supplyRisk' | 'recycling' | null>(null)
watch(
  () => props.metal.slug,
  () => {
    openImpact.value = null
    openRisk.value = null
  },
)

function handlePlayVideo() {
  if (!props.metal.video) return
  trackClick({
    zone: props.zone,
    name: props.metal.slug,
    campaign: 'raw-materials',
  })
}
</script>
